import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, ArrowUpRight } from 'lucide-react';

/**
 * Navbar Component - Following LanceSoft Brand Guidelines
 * 
 * Brand Colors:
 * - Navy: #153860 (primary background)
 * - Teal: #55C3BD (accent, logo subtitle)
 * - Orange: #EE7623 (CTA buttons)
 * - Blue: #0079C2 (links)
 */

const Navbar: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavClick = (sectionId: string) => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
    };

    const scrollToContact = () => {
        document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
    };

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
                ? 'bg-[#153860]/95 backdrop-blur-md border-b border-white/10 py-2 shadow-lg'
                : 'bg-[#153860] py-4'
                }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo - Stacked layout matching other subsites */}
                    <div
                        className="flex flex-col items-start flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                        <img src="/lancesoft-logo.png" alt="LanceSoft" className="h-8 brightness-0 invert" />
                        <span className="text-[10px] tracking-[0.15em] text-[#55C3BD] uppercase font-medium mt-1">VLSI</span>
                    </div>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center space-x-6">
                        <button onClick={() => handleNavClick('differentiators')} className="text-slate-300 hover:text-[#55C3BD] transition-colors text-sm font-medium uppercase tracking-wider relative group">
                            Why LanceSoft
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#55C3BD] transition-all group-hover:w-full"></span>
                        </button>
                        <button onClick={() => handleNavClick('capabilities')} className="text-slate-300 hover:text-[#55C3BD] transition-colors text-sm font-medium uppercase tracking-wider relative group">
                            Capabilities
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#55C3BD] transition-all group-hover:w-full"></span>
                        </button>
                        <button onClick={() => handleNavClick('engagement')} className="text-slate-300 hover:text-[#55C3BD] transition-colors text-sm font-medium uppercase tracking-wider relative group">
                            Engagement
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#55C3BD] transition-all group-hover:w-full"></span>
                        </button>

                        <div className="h-6 w-px bg-white/20 mx-2"></div>

                        {/* Global Site Link */}
                        <a
                            href="../"
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white transition-colors"
                        >
                            <Globe size={14} /> Global Site
                        </a>

                        {/* CTA - Orange per brand guidelines */}
                        <button
                            onClick={scrollToContact}
                            className="bg-[#EE7623] hover:bg-white hover:text-[#EE7623] text-white px-5 py-2 rounded text-sm font-semibold transition-all duration-300 shadow-lg"
                        >
                            Contact Us
                        </button>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="lg:hidden">
                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-2 hover:text-[#55C3BD]">
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="lg:hidden absolute top-full left-0 right-0 bg-[#153860] border-b border-white/10 p-6 flex flex-col space-y-4 shadow-xl">
                    <button onClick={() => handleNavClick('differentiators')} className="text-left text-white/80 hover:text-[#55C3BD] font-medium text-base py-2">Why LanceSoft</button>
                    <button onClick={() => handleNavClick('capabilities')} className="text-left text-white/80 hover:text-[#55C3BD] font-medium text-base py-2">Capabilities</button>
                    <button onClick={() => handleNavClick('engagement')} className="text-left text-white/80 hover:text-[#55C3BD] font-medium text-base py-2">Engagement</button>

                    <div className="border-t border-white/10 pt-4 mt-2">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-2 font-bold">Our Ecosystem</p>
                        <a href="../" className="flex items-center gap-2 text-slate-300 py-2 hover:text-white">
                            <Globe size={16} /> Global Site <ArrowUpRight size={14} />
                        </a>
                    </div>

                    <button
                        onClick={scrollToContact}
                        className="bg-[#EE7623] text-white px-6 py-3 rounded text-center font-semibold mt-4"
                    >
                        Contact Us
                    </button>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
